import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  ArrowRight, 
  CheckCircle2,
  Star,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  Sparkles,
  Quote,
  Play,
  Ticket,
  CalendarDays,
  Locate
} from 'lucide-react';
import { getUpcomingEventDates, getDefaultEventDate, detectZipFromIP, formatDateShort } from '../utils/smartDefaults';
import SEO from '../components/SEO';
import { organizationLD, websiteLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback hero background
const HERO_BG = "https://images.unsplash.com/photo-1531058020387-3be344556be6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBlb3BsZSUyMG5ldHdvcmtpbmclMjBldmVudHxlbnwwfHx8fDE3NzQzMTA1NDN8MA&ixlib=rb-4.1.0&q=85";

// Service category images
const SERVICE_IMAGES = {
  catering: "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800",
  bar: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=800",
  entertainment: "https://images.unsplash.com/photo-1571266028243-3716f02d2d2e?w=800",
  staffing: "https://images.unsplash.com/photo-1516997121675-4c2d1684aa3e?w=800"
};

// Duration options
const DURATION_OPTIONS = [
  { hours: 2, label: '2 hours' },
  { hours: 3, label: '3 hours' },
  { hours: 4, label: '4 hours' },
  { hours: 5, label: '5 hours' },
  { hours: 6, label: '6 hours' },
  { hours: 8, label: '8 hours' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Booking bar state
  const [eventDetails, setEventDetails] = useState({
    date: getDefaultEventDate(),
    time: '18:00',
    duration: 4,
    guestCount: 50,
    venueZipCode: ''
  });

  const suggestedDates = getUpcomingEventDates(4);
  
  // Auto-detect zip code from IP
  useEffect(() => {
    detectZipFromIP().then(zip => {
      if (zip) setEventDetails(prev => prev.venueZipCode ? prev : { ...prev, venueZipCode: zip });
    });
  }, []);
  
  // Popover states
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [timePopoverOpen, setTimePopoverOpen] = useState(false);
  const [guestsPopoverOpen, setGuestsPopoverOpen] = useState(false);
  const [locationPopoverOpen, setLocationPopoverOpen] = useState(false);
  
  // Featured services
  const [featuredServices, setFeaturedServices] = useState([]);
  
  // Seasonal specials
  const [seasonalProducts, setSeasonalProducts] = useState([]);
  const seasonalRef = useRef(null);
  
  // Upcoming Events
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const carouselRef = useRef(null);

  // Hero image gallery
  const [heroImages, setHeroImages] = useState([]);
  const [activeHeroIdx, setActiveHeroIdx] = useState(0);

  // Fetch hero images
  useEffect(() => {
    axios.get(`${API}/hero-images`).then(res => {
      const imgs = res.data?.images || [];
      if (imgs.length > 0) setHeroImages(imgs);
    }).catch(() => {});
  }, []);

  // Auto-cycle hero images every 6 seconds
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setActiveHeroIdx(prev => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const currentHeroBg = heroImages.length > 0 ? heroImages[activeHeroIdx]?.url : HERO_BG;
  const currentHeroAlt = heroImages.length > 0 ? heroImages[activeHeroIdx]?.alt_text : 'Professional event';
  
  // Fetch featured services + seasonal products
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/services/products`);
        const featured = (response.data.products || []).filter(p => p.is_featured).slice(0, 4);
        setFeaturedServices(featured);
      } catch (error) {
        console.error('Failed to fetch featured services:', error);
      }
    };
    const fetchSeasonal = async () => {
      try {
        const response = await axios.get(`${API}/services/seasonal`);
        setSeasonalProducts(response.data.products || []);
      } catch (error) {
        console.error('Failed to fetch seasonal products:', error);
      }
    };
    fetchFeatured();
    fetchSeasonal();
  }, []);

  // Fetch upcoming events
  useEffect(() => {
    const fetchUpcomingEvents = async () => {
      try {
        const response = await axios.get(`${API}/hosted-events/upcoming?limit=8`);
        // API returns a list directly, not an object with events property
        setUpcomingEvents(Array.isArray(response.data) ? response.data : (response.data.events || []));
      } catch (error) {
        console.error('Failed to fetch upcoming events:', error);
      } finally {
        setEventsLoading(false);
      }
    };
    fetchUpcomingEvents();
  }, []);

  // Carousel scroll handlers
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = direction === 'left' ? -340 : 340;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollSeasonal = (direction) => {
    if (seasonalRef.current) {
      const scrollAmount = direction === 'left' ? -360 : 360;
      seasonalRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Format functions
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return 'Select date';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTimeDisplay = (time, duration) => {
    if (!time) return 'Select time';
    const [hours, minutes] = time.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes);
    const endDate = new Date(startDate.getTime() + duration * 60 * 60 * 1000);
    const formatTime = (d) => d.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return `${formatTime(startDate)} to ${formatTime(endDate)}`;
  };

  // Handle get quote
  const handleGetQuote = () => {
    // Build query params if we have details
    const params = new URLSearchParams();
    if (eventDetails.date) params.set('date', eventDetails.date);
    if (eventDetails.time) params.set('time', eventDetails.time);
    if (eventDetails.duration) params.set('duration', eventDetails.duration);
    if (eventDetails.guestCount) params.set('guests', eventDetails.guestCount);
    if (eventDetails.venueZipCode) params.set('zip', eventDetails.venueZipCode);
    
    navigate(`/plan-event${params.toString() ? '?' + params.toString() : ''}`);
  };

  const testimonials = [
    {
      quote: "Eventism transformed how we plan our quarterly gatherings. The instant quotes save us hours of back-and-forth with vendors.",
      author: "Sarah Chen",
      role: "Events Director",
      company: "TechFlow Inc."
    },
    {
      quote: "Finally, a platform that understands the demands of high-volume event planning. Professional, reliable, exceptional.",
      author: "Michael Torres",
      role: "Operations Lead",
      company: "Summit Partners"
    },
    {
      quote: "From team celebrations to client dinners, Eventism delivers consistently excellent experiences every single time.",
      author: "Jennifer Walsh",
      role: "Executive Assistant",
      company: "Meridian Group"
    }
  ];

  const stats = [
    { number: "500+", label: "Events Delivered" },
    { number: "50K+", label: "Guests Served" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "24hr", label: "Quote Response" }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Eventism — Book Caterers, DJs, Venues & Event Staff Instantly"
        description="Eventism is the all-in-one events platform. Browse vetted caterers, DJs, photographers, venues, and event staff in Denver, Las Vegas, Los Angeles & Charlotte. Real pricing, instant booking, escrow protection."
        keywords={[
          'event planning platform',
          'book event services online',
          'Denver event planning',
          'Las Vegas event services',
          'Los Angeles event planners',
          'Charlotte event services',
          'wedding planner platform',
          'corporate event services',
          'party planning platform',
          'event catering booking',
          'DJ booking platform',
          'venue marketplace',
          'event staff hire',
          'Eventism',
        ]}
        type="website"
        structuredData={[organizationLD(), websiteLD()]}
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with crossfade */}
        <div className="absolute inset-0">
          {heroImages.length > 1 ? (
            heroImages.map((img, idx) => (
              <img
                key={img.image_id}
                src={img.url}
                alt={img.alt_text || 'Event'}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                style={{ opacity: idx === activeHeroIdx ? 1 : 0 }}
              />
            ))
          ) : (
            <img
              src={currentHeroBg}
              alt={currentHeroAlt}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        </div>

        {/* Hero dots indicator */}
        {heroImages.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="hero-gallery-dots">
            {heroImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveHeroIdx(idx)}
                className={`w-2 h-2 rounded-full transition-all ${idx === activeHeroIdx ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'}`}
                data-testid={`hero-dot-${idx}`}
              />
            ))}
          </div>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 pb-12">
          {/* Hero Content */}
          <div className="mb-12">
            {/* Tagline Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
              <Sparkles className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium text-foreground">Denver's Premier Event Partner</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6" data-testid="hero-headline">
              Exceptional Events.
              <br />
              <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                Instant Quotes.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="max-w-2xl mx-auto text-lg sm:text-xl text-foreground/80 mb-4">
              From catering to entertainment, we handle every detail so you can focus on what matters.
            </p>
            <p className="text-foreground/60 text-sm mb-8">
              Our calendar fills fast. Secure your date with an instant quote.
            </p>
          </div>

          {/* Booking Bar */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden" data-testid="hero-booking-bar">
              <div className="grid grid-cols-1 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-gray-200">
                {/* Date */}
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left w-full">
                      <CalendarDays className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Date</p>
                        <p className="font-semibold text-gray-900 truncate">
                          {formatDateDisplay(eventDetails.date)}
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start">
                    <div className="space-y-3">
                      <p className="font-medium text-sm">Event Date</p>
                      <Input
                        type="date"
                        value={eventDetails.date}
                        onChange={(e) => setEventDetails({ ...eventDetails, date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        className="text-sm"
                      />
                      {suggestedDates.length > 0 && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">Popular Upcoming Dates</p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {suggestedDates.map(sd => (
                              <button key={sd.date}
                                onClick={() => setEventDetails({ ...eventDetails, date: sd.date })}
                                className={`text-left p-2 rounded-lg border text-xs transition-all ${eventDetails.date === sd.date ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-200 hover:border-indigo-200 hover:bg-gray-50'}`}
                                data-testid={`quick-date-${sd.date}`}>
                                <p className="font-medium truncate">{sd.label}</p>
                                <p className="text-[10px] text-muted-foreground">{formatDateShort(sd.date)}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <Button size="sm" className="w-full" onClick={() => setDatePopoverOpen(false)}>Done</Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Time */}
                <Popover open={timePopoverOpen} onOpenChange={setTimePopoverOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left w-full">
                      <Clock className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Time</p>
                        <p className="font-semibold text-gray-900 truncate">
                          {formatTimeDisplay(eventDetails.time, eventDetails.duration)}
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-4" align="start">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <p className="font-medium text-sm">Start Time</p>
                        <Input
                          type="time"
                          value={eventDetails.time}
                          onChange={(e) => setEventDetails({ ...eventDetails, time: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="font-medium text-sm">Duration</p>
                        <div className="grid grid-cols-3 gap-2">
                          {DURATION_OPTIONS.map(opt => (
                            <Button
                              key={opt.hours}
                              variant={eventDetails.duration === opt.hours ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setEventDetails({ ...eventDetails, duration: opt.hours })}
                              className="text-xs"
                            >
                              {opt.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <Button size="sm" className="w-full" onClick={() => setTimePopoverOpen(false)}>
                        Done
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Guests */}
                <Popover open={guestsPopoverOpen} onOpenChange={setGuestsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left w-full">
                      <Users className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Guests</p>
                        <p className="font-semibold text-gray-900">
                          {eventDetails.guestCount} people
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4" align="start">
                    <div className="space-y-4">
                      <p className="font-medium text-sm">Number of Guests</p>
                      <div className="flex items-center gap-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setEventDetails({ 
                            ...eventDetails, 
                            guestCount: Math.max(1, eventDetails.guestCount - 10) 
                          })}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          value={eventDetails.guestCount}
                          onChange={(e) => setEventDetails({ 
                            ...eventDetails, 
                            guestCount: Math.max(1, parseInt(e.target.value) || 1) 
                          })}
                          className="text-center"
                          min="1"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setEventDetails({ 
                            ...eventDetails, 
                            guestCount: eventDetails.guestCount + 10 
                          })}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {[25, 50, 100, 200].map(num => (
                          <Button
                            key={num}
                            variant={eventDetails.guestCount === num ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setEventDetails({ ...eventDetails, guestCount: num })}
                            className="text-xs"
                          >
                            {num}
                          </Button>
                        ))}
                      </div>
                      <Button size="sm" className="w-full" onClick={() => setGuestsPopoverOpen(false)}>
                        Done
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Location */}
                <Popover open={locationPopoverOpen} onOpenChange={setLocationPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left w-full">
                      <MapPin className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Location</p>
                        <p className="font-semibold text-gray-900 truncate">
                          {eventDetails.venueZipCode || 'Detecting...'}
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4" align="end">
                    <div className="space-y-3">
                      <p className="font-medium text-sm">Venue ZIP Code</p>
                      <div className="relative">
                        <Input
                          type="text"
                          value={eventDetails.venueZipCode}
                          onChange={(e) => setEventDetails({ ...eventDetails, venueZipCode: e.target.value })}
                          placeholder="e.g., 80202"
                          maxLength={10}
                        />
                        <button onClick={() => detectZipFromIP().then(z => z && setEventDetails(p => ({ ...p, venueZipCode: z })))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-500 hover:text-indigo-600 p-1" title="Auto-detect from location">
                          <Locate className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Auto-detected from your location</p>
                      <Button size="sm" className="w-full" onClick={() => setLocationPopoverOpen(false)}>Done</Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* CTA Button */}
                <div className="p-3 flex items-center justify-center bg-indigo-500">
                  <Button 
                    size="lg"
                    className="w-full h-full bg-indigo-600 hover:bg-indigo-700 text-foreground font-semibold rounded-xl"
                    onClick={handleGetQuote}
                    data-testid="hero-get-quote-btn"
                  >
                    Get Instant Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-6 flex items-center justify-center gap-6 text-foreground/70 text-sm">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                Free quotes
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                No obligations
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                Response in 24hrs
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Host Events Band — directs prospective organizers to /sell-tickets */}
      <section className="relative py-12 bg-gradient-to-r from-amber-500/10 via-fuchsia-500/10 to-indigo-500/10 border-y border-border" data-testid="host-events-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-500 flex items-center gap-2">
                <Ticket className="h-3.5 w-3.5" /> For organizers
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
                Host events on Eventism.
              </h2>
              <p className="text-sm text-muted-foreground max-w-xl">
                Sell tickets in 60 seconds. Lower fees than Eventbrite, built-in marketing, and $25 free wallet credit when you sign up.
              </p>
            </div>
            <Link to="/sell-tickets" data-testid="landing-host-events-cta">
              <Button className="h-11 px-6 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0 shadow-lg shadow-fuchsia-500/20 shrink-0">
                <Sparkles className="h-4 w-4 mr-2" /> Start selling tickets <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl font-bold text-amber-400">{stat.number}</p>
                <p className="text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seasonal Specials Carousel */}
      {seasonalProducts.length > 0 && (
        <section className="py-16 bg-background relative overflow-hidden" data-testid="seasonal-specials-section">
          {/* Subtle seasonal accent */}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-rose-500/5 pointer-events-none" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="flex items-center justify-between mb-8">
              <div>
                <Badge className="mb-3 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 hover:bg-amber-500/20">
                  Limited Time
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold">
                  Seasonal Specials
                </h2>
                <p className="text-muted-foreground mt-2">
                  Book now — these curated packages are only available for a limited time
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full"
                  onClick={() => scrollSeasonal('left')}
                  data-testid="seasonal-prev"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full"
                  onClick={() => scrollSeasonal('right')}
                  data-testid="seasonal-next"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div 
              ref={seasonalRef}
              className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {seasonalProducts.map(product => {
                const plusTier = product.option_groups?.find(g => g.name === 'Package Tier')?.choices?.find(c => !c.is_default);
                const seasonTag = (product.tags || []).find(t => !['seasonal'].includes(t)) || product.sub_category;
                
                return (
                  <Card 
                    key={product.service_id}
                    className="flex-shrink-0 w-80 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group snap-start border-amber-500/20 hover:border-amber-500/40"
                    onClick={() => navigate(`/plan-event?zip=80202&service=${product.service_id}`)}
                    data-testid={`seasonal-product-${product.service_id}`}
                  >
                    <div className="h-44 overflow-hidden relative">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <Badge className="absolute top-3 left-3 bg-amber-500 text-white border-0">
                        {seasonTag?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                      {plusTier && (
                        <div className="absolute bottom-3 right-3 text-white text-sm font-semibold bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
                          From ${product.base_price}/pp
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-lg mb-1 group-hover:text-amber-500 transition-colors line-clamp-1">
                        {product.name}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {product.short_description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm text-muted-foreground">Basic </span>
                          <span className="font-bold text-amber-600 dark:text-amber-400">${product.base_price}</span>
                          {plusTier && (
                            <>
                              <span className="text-sm text-muted-foreground"> · Plus </span>
                              <span className="font-bold text-amber-600 dark:text-amber-400">${product.base_price + plusTier.price_modifier}</span>
                            </>
                          )}
                          <span className="text-xs text-muted-foreground">/pp</span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* View All Card */}
              <Card 
                className="flex-shrink-0 w-64 overflow-hidden cursor-pointer group snap-start bg-gradient-to-br from-amber-500 to-rose-500 border-0 flex items-center justify-center min-h-[320px]"
                onClick={() => navigate('/plan-event?zip=80202')}
                data-testid="seasonal-view-all"
              >
                <CardContent className="text-center">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <ArrowRight className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg text-white mb-1">View All Packages</h3>
                  <p className="text-white/70 text-sm">Explore the full menu</p>
                </CardContent>
              </Card>
            </div>

            {/* Mobile Navigation */}
            <div className="flex md:hidden items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => scrollSeasonal('left')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full" onClick={() => scrollSeasonal('right')}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Services Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Our Services</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Everything You Need, One Partner
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From gourmet catering to premium bar service, professional entertainment to dedicated staff - 
              we deliver exceptional experiences for every occasion.
            </p>
          </div>

          {featuredServices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredServices.map((service) => (
                <Card 
                  key={service.service_id} 
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  onClick={() => navigate('/plan-event')}
                  data-testid={`featured-service-${service.service_id}`}
                >
                  <div className="h-48 overflow-hidden relative">
                    <img
                      src={service.image_url}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {service.is_featured && (
                      <Badge className="absolute top-3 right-3 bg-amber-500">Featured</Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{service.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {service.short_description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-indigo-500 font-bold">
                        From ${service.base_price}
                        <span className="text-sm font-normal text-muted-foreground">
                          /{service.pricing_model === 'per_person' ? 'person' : 
                            service.pricing_model === 'per_hour' ? 'hr' : 
                            service.pricing_model === 'per_hour_per_person' ? 'hr/person' : ''}
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-indigo-500 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { name: "Gourmet Catering", desc: "From elegant plated dinners to interactive food stations", img: SERVICE_IMAGES.catering, price: "18" },
                { name: "Premium Bar Service", desc: "Craft cocktails, fine wines, and expert mixologists", img: SERVICE_IMAGES.bar, price: "15" },
                { name: "Live Entertainment", desc: "DJs, live bands, and unforgettable performances", img: SERVICE_IMAGES.entertainment, price: "200" },
                { name: "Professional Staff", desc: "Trained servers, bartenders, and event coordinators", img: SERVICE_IMAGES.staffing, price: "45" }
              ].map((service, idx) => (
                <Card 
                  key={idx} 
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  onClick={() => navigate('/plan-event')}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={service.img}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{service.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{service.desc}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-indigo-500 font-bold">From ${service.price}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-indigo-500 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Button 
              size="lg" 
              onClick={() => navigate('/plan-event')}
              className="rounded-full px-8"
              data-testid="view-all-services-btn"
            >
              View All Services <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50 dark:bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Why Eventism</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                We Handle the Details.
                <br />
                <span className="text-indigo-500">You Enjoy the Moment.</span>
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: "Instant Pricing",
                    desc: "No more waiting days for quotes. Get accurate pricing in seconds with our smart calculator."
                  },
                  {
                    title: "Vetted Professionals",
                    desc: "Every vendor and staff member in our network is carefully screened and trained."
                  },
                  {
                    title: "Full-Service Coordination",
                    desc: "From planning to execution, we manage every detail so your event runs flawlessly."
                  },
                  {
                    title: "Flexible Customization",
                    desc: "Tailor every aspect of your event - menus, timing, services - to your exact needs."
                  }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="h-5 w-5 text-indigo-500" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1768508948485-a7adc1f3427f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHBlb3BsZSUyMG5ldHdvcmtpbmclMjBldmVudHxlbnwwfHx8fDE3NzQzMTA1NDN8MA&ixlib=rb-4.1.0&q=85"
                alt="People enjoying an event"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-muted rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 border-2 border-white" />
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">500+ Events</p>
                    <p className="text-xs text-muted-foreground">Delivered this year</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Testimonials</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold">
              Trusted by Event Planners
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((item, idx) => (
              <Card key={idx} className="p-6 relative">
                <Quote className="h-8 w-8 text-indigo-200 absolute top-4 right-4" />
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 italic">"{item.quote}"</p>
                <div>
                  <p className="font-semibold">{item.author}</p>
                  <p className="text-sm text-muted-foreground">{item.role}, {item.company}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events Carousel */}
      {upcomingEvents.length > 0 && (
        <section className="py-20 bg-card" data-testid="upcoming-events-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <Badge variant="outline" className="mb-3 text-amber-400 border-amber-400/30">Eventism Events</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                  Upcoming Events
                </h2>
                <p className="text-muted-foreground mt-2">
                  Join us at our curated parties, festivals, and experiences
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full border-border text-muted-foreground hover:bg-muted"
                  onClick={() => scrollCarousel('left')}
                  data-testid="carousel-prev"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full border-border text-muted-foreground hover:bg-muted"
                  onClick={() => scrollCarousel('right')}
                  data-testid="carousel-next"
                >
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Carousel */}
            <div 
              ref={carouselRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {upcomingEvents.map(event => (
                <Card 
                  key={event.event_id}
                  className="flex-shrink-0 w-80 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group snap-start bg-muted/50 border-border"
                  onClick={() => navigate(`/events/${event.city}/${event.slug}`)}
                  data-testid={`upcoming-event-${event.event_id}`}
                >
                  <div className="h-44 overflow-hidden relative">
                    {event.image_url ? (
                      <img
                        src={event.image_url}
                        alt={event.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <Ticket className="h-12 w-12 text-foreground/50" />
                      </div>
                    )}
                    {event.is_featured && (
                      <Badge className="absolute top-3 left-3 bg-amber-500">Featured</Badge>
                    )}
                    {event.ticket_price_min && (
                      <Badge className="absolute bottom-3 right-3 bg-black/60 text-foreground">
                        From ${event.ticket_price_min}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs bg-indigo-500/20 text-indigo-300">
                        {event.category || 'Event'}
                      </Badge>
                      {event.age_restriction && (
                        <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                          {event.age_restriction}
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg text-foreground mb-1 group-hover:text-indigo-400 transition-colors line-clamp-1">
                      {event.name}
                    </h3>
                    {event.tagline && (
                      <p className="text-sm text-muted-foreground line-clamp-1 mb-3">{event.tagline}</p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-indigo-400" />
                        {formatEventDate(event.event_date)}
                      </span>
                      {event.venue_name && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                          <span className="truncate">{event.venue_name}</span>
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* View All Card */}
              <Card 
                className="flex-shrink-0 w-80 overflow-hidden cursor-pointer group snap-start bg-gradient-to-br from-indigo-600 to-purple-700 border-0 flex items-center justify-center min-h-[320px]"
                onClick={() => navigate('/events')}
                data-testid="view-all-events-card"
              >
                <CardContent className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <ArrowRight className="h-8 w-8 text-foreground" />
                  </div>
                  <h3 className="font-semibold text-xl text-foreground mb-2">View All Events</h3>
                  <p className="text-foreground/70 text-sm">Browse our full calendar</p>
                </CardContent>
              </Card>
            </div>

            {/* Mobile Navigation */}
            <div className="flex md:hidden items-center justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="icon"
                className="rounded-full border-border text-muted-foreground"
                onClick={() => scrollCarousel('left')}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full border-border text-muted-foreground"
                onClick={() => scrollCarousel('right')}
              >
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Ready to Create Something Exceptional?
          </h2>
          <p className="text-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of planners who trust Eventism for their most important gatherings. 
            Get your instant quote today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-indigo-600 hover:bg-white/90 rounded-full px-8"
              onClick={() => navigate('/plan-event')}
              data-testid="cta-get-quote"
            >
              Get Your Instant Quote <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-foreground hover:bg-white/10 rounded-full px-8"
              onClick={() => navigate('/marketplace')}
            >
              Browse Our Vendors
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
